import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { api } from '../App';
import { setPageMeta, statusText } from './Landing';
import { metasDeRuta } from '../lib/metas';
import { Imagen, precargarImagen } from '../components/Imagen';
import FormularioHubSpot from '../components/FormularioHubSpot';
import { C, SERIF, SANS, MONO, TRACK, MEDIDA, FOTO } from '../lib/tokens';

const LOGO_NEG = '/brand/soha-horizontal-marfil.png';
const LOGO_POS = '/brand/soha-horizontal-verde.png';

const UI = {
  es: {
    back: 'Volver a la colección', access: 'Acceso copropietarios',
    place: 'El lugar', authorship: 'La autoría', studioLabel: 'Despacho de arquitectura',
    essentials: 'Datos esenciales', sqm: 'Superficie', bedrooms: 'Recámaras', weeks: 'Uso al año', shares: 'Copropiedades',
    map: 'Ubicación', dossierCta: 'Solicitar el dossier privado',
    dossierIntro: 'Recibe el dossier privado de esta casa. Alguien del equipo te escribe personalmente en menos de 24 horas.',
    name: 'Nombre completo', email: 'Correo electrónico', phone: 'Teléfono', send: 'Solicitar el dossier', sending: 'Enviando...',
    successTitle: 'Gracias.', successBody: 'Recibimos tu solicitud. Te enviaremos el dossier privado muy pronto.',
    errName: 'Por favor escribe tu nombre.', errEmail: 'Escribe un correo válido.',
    notFound: 'No encontramos esta casa.', backHome: 'Volver al inicio',
  },
  en: {
    back: 'Back to the collection', access: 'Owner access',
    place: 'The place', authorship: 'Authorship', studioLabel: 'Architecture studio',
    essentials: 'Essentials', sqm: 'Area', bedrooms: 'Bedrooms', weeks: 'Yearly use', shares: 'Co-ownerships',
    map: 'Location', dossierCta: 'Request the private dossier',
    dossierIntro: 'Receive the private dossier for this home. Someone from the team will write to you personally within 24 hours.',
    name: 'Full name', email: 'Email', phone: 'Phone', send: 'Request the dossier', sending: 'Sending...',
    successTitle: 'Thank you.', successBody: 'We received your request. We will send you the private dossier very soon.',
    errName: 'Please enter your name.', errEmail: 'Enter a valid email.',
    notFound: 'We could not find this house.', backHome: 'Back to home',
  },
};

const IMG_FILTER = FOTO;

/* Slug de la casa → valor del campo oculto crato_proyecto en HubSpot.
   Nota: CASA_TAU y CASA_IXI aún no existen como valores en HubSpot; el
   componente valida contra PROYECTOS_PERMITIDOS y no escribe el campo. */
const PROPIEDAD_HUBSPOT = {
  'casa-tau': 'CASA_TAU',
  'casa-mara': 'CASA_MARA',
  'casa-ixi': 'CASA_IXI',
};

export default function CasaDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const [content, setContent] = useState(null);
  const [lang, setLang] = useState('es');
  const [scrolled, setScrolled] = useState(false);
  const [esMovil, setEsMovil] = useState(() => typeof window !== 'undefined' && window.innerWidth < 1024);

  useEffect(() => {
    api.get('/landing/content').then(setContent).catch(() => setContent({ collection: { items: [] } }));
  }, []);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    const onResize = () => setEsMovil(window.innerWidth < 1024);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, []);
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  const t = UI[lang];
  const house = content?.collection?.items?.find((h) => h.slug === slug);

  useEffect(() => {
    const hero = house?.hero_image || house?.image_url;
    if (hero) precargarImagen(hero, '100vw');
  }, [house]);

  useEffect(() => {
    const metas = metasDeRuta(`/casa/${slug}`);
    if (metas) setPageMeta(metas);
    else if (house) setPageMeta({
      title: `${house.name} · ${house.location} — SOHA Reserve`,
      description: house.sensory || house.place_text,
      image: house.hero_image || house.image_url,
      url: window.location.href,
    });
  }, [house, slug]);

  if (!content) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: C.bg }}>
        <img src={LOGO_POS} alt="SOHA Reserve" width="800" height="318" className="h-10 w-auto animate-pulse" style={{ filter: 'none' }} />
      </div>
    );
  }

  if (!house) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 px-6 text-center" style={{ background: C.bg, fontFamily: SANS }}>
        <p className="text-3xl" style={{ fontFamily: SERIF, color: C.ink }}>{t.notFound}</p>
        <Link to="/" className="text-sm tracking-[0.1em] underline underline-offset-4" style={{ color: C.accent }}>{t.backHome}</Link>
      </div>
    );
  }

  const st = statusText(house, lang);
  const specs = house.specs || {};
  const gallery = (house.gallery || []).filter(Boolean);
  // Si la casa aún no tiene galería, usamos su imagen principal para no dejar la columna vacía.
  const imagenLugar = gallery[0] || house.image_url || house.hero_image;
  const sinDefinir = (v) => !v || /^por definir$/i.test(String(v).trim());
  const mostrarAutoria = !sinDefinir(house.authorship_studio) || !sinDefinir(house.authorship_text);
  // En movil la barra es siempre marfil: el hero dividido no lleva foto detras.
  const barraSolida = scrolled || esMovil;
  const navColor = barraSolida ? C.ink : C.linen;

  return (
    <div style={{ fontFamily: SANS, background: C.bg, color: C.ink }} data-testid="casa-detail">
      {/* Header */}
      <header
        className="fixed top-0 inset-x-0 z-50 transition-all duration-500"
        style={{
          background: barraSolida ? C.linen92 : 'transparent',
          backdropFilter: barraSolida ? 'blur(12px)' : 'none',
          borderBottom: barraSolida ? `1px solid ${C.line}` : '1px solid transparent',
        }}
        data-testid="casa-header"
      >
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 h-[72px] flex items-center justify-between gap-4">
          <Link to="/" className="shrink-0" data-testid="casa-logo">
            <img src={barraSolida ? LOGO_POS : LOGO_NEG} alt="SOHA Reserve" width="800" height="318" className="h-7 md:h-9 w-auto object-contain transition-opacity" style={{ filter: 'none', minWidth: 120, transitionDuration: 'var(--soha-dur)' }} />
          </Link>
          <div className="flex items-center gap-4 md:gap-5 shrink-0">
            <button onClick={() => setLang(lang === 'es' ? 'en' : 'es')} className="text-xs tracking-[0.2em] hover:opacity-60 transition-opacity" style={{ color: navColor }} data-testid="casa-lang-toggle">
              {lang === 'es' ? 'EN' : 'ES'}
            </button>
            <span className="hidden md:inline-block w-px h-3.5" style={{ background: barraSolida ? C.line : C.linen30 }} />
            <Link to="/login" className="hidden md:inline text-[10px] tracking-[0.18em] uppercase opacity-70 hover:opacity-100 transition-opacity" style={{ color: navColor }} data-testid="casa-owner-access">
              {t.access}
            </Link>
          </div>
        </div>
      </header>

      {/* Hero movil: foto en perspectiva natural (4:3) y texto sobre marfil. */}
      <section className="lg:hidden" data-testid="casa-hero-movil">
        <div className="pt-[72px]" style={{ background: C.bg }} />
        <div className="relative w-full overflow-hidden md:max-h-[52vh]" style={{ aspectRatio: '3 / 2' }}>
          <Imagen src={house.hero_image || house.image_url} alt={house.name} ratio={3 / 2} sizes="100vw" prioridad className="absolute inset-0 w-full h-full object-cover object-center" style={{ filter: IMG_FILTER }} />
        </div>
        <div className="px-6 md:px-12 pt-7 md:pt-10 pb-10 md:pb-14" style={{ background: C.bg }}>
          {st && <span className="inline-block mb-5 px-3 py-1 text-[13px] uppercase" style={{ background: C.bg2, color: house.status === 'completa' ? C.ink40 : C.accent, letterSpacing: TRACK.caption }} data-testid="casa-status-movil">{st}</span>}
          <p className="text-[13px] uppercase mb-3" style={{ color: C.ink60, letterSpacing: TRACK.caption }} data-testid="casa-lockup-movil">
            SOHA Reserve &middot; {house.name}
          </p>
          <h1 className="text-4xl md:text-5xl leading-[1.06]" style={{ fontFamily: SERIF, fontWeight: 300, color: C.accent, letterSpacing: TRACK.h1 }} data-testid="casa-name-movil">{house.name}</h1>
          <p className="mt-3 text-[13px] uppercase" style={{ color: C.ink40, letterSpacing: TRACK.caption }}>{house.location}</p>
          <p className="mt-5 text-sm leading-relaxed" style={{ color: C.ink60, maxWidth: MEDIDA }}>{house.sensory}</p>
        </div>
      </section>

      {/* Hero escritorio */}
      <section className="relative h-screen overflow-hidden hidden lg:block" data-testid="casa-hero">
        <Imagen src={house.hero_image || house.image_url} alt={house.name} ratio={16 / 9} sizes="100vw" prioridad className="absolute inset-0 w-full h-full object-cover" style={{ filter: IMG_FILTER }} />
        <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${C.scrim72} 0%, ${C.scrim55} 22%, ${C.scrim15} 55%, ${C.scrim28} 100%)` }} />
        <div className="relative z-10 h-full max-w-[1400px] mx-auto px-6 md:px-12 flex flex-col justify-end pb-16 md:pb-24">
          <motion.div initial={reduce ? false : { opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}>
            {st && <span className="inline-block mb-5 px-3 py-1 text-[13px] uppercase" style={{ background: C.linen92, color: house.status === 'completa' ? C.ink40 : C.accent, letterSpacing: TRACK.caption }} data-testid="casa-status">{st}</span>}
            <p className="text-[13px] uppercase mb-4" style={{ color: C.linen82, letterSpacing: TRACK.caption }} data-testid="casa-lockup">
              SOHA Reserve &middot; {house.name}
            </p>
            <h1 className="text-5xl md:text-7xl leading-[1.02]" style={{ fontFamily: SERIF, fontWeight: 300, color: C.linen, letterSpacing: TRACK.h1 }} data-testid="casa-name">{house.name}</h1>
            <p className="mt-4 text-[13px] uppercase" style={{ color: C.linen65, letterSpacing: TRACK.caption }}>{house.location}</p>
            <p className="mt-5 text-base md:text-lg" style={{ color: C.linen82, maxWidth: MEDIDA }}>{house.sensory}</p>
          </motion.div>
        </div>
      </section>

      {/* Back link */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-10">
        <Link to="/" className="inline-flex items-center gap-2 text-xs tracking-[0.14em] uppercase hover:opacity-60 transition-opacity" style={{ color: C.ink60 }} data-testid="casa-back">
          <ArrowLeft className="h-3.5 w-3.5" /> {t.back}
        </Link>
      </div>

      {/* El lugar */}
      {house.place_text && (
        <section className="py-16 md:py-28 px-6 md:px-12" data-testid="casa-place">
          <div className="max-w-[1400px] mx-auto grid md:grid-cols-2 gap-12 md:gap-20 items-center">
            <div>
              <p style={{ color: C.gold, letterSpacing: TRACK.eyebrow }} className="text-[13px] uppercase">{t.place}</p>
              <p className="mt-6 text-2xl md:text-3xl leading-[1.4]" style={{ fontFamily: SERIF, fontWeight: 300 }}>{house.place_text}</p>
            </div>
            {imagenLugar && <div className="relative overflow-hidden" style={{ aspectRatio: '4/5' }}><Imagen src={imagenLugar} alt={house.name} ratio={4 / 5} sizes="(max-width: 768px) 100vw, 50vw" className="w-full h-full object-cover" style={{ filter: IMG_FILTER }} /></div>}
          </div>
        </section>
      )}

      {/* Galería editorial */}
      {gallery.length > 1 && (
        <section className="pb-8 md:pb-16 px-6 md:px-12" data-testid="casa-gallery">
          <div className="max-w-[1400px] mx-auto grid md:grid-cols-2 gap-6 md:gap-8">
            {gallery.slice(1).map((g, i) => (
              <div key={g || `gal-${i}`} className="relative overflow-hidden" style={{ aspectRatio: i % 2 === 0 ? '3/4' : '4/3' }}>
                <Imagen src={g} alt={`${house.name} ${i + 2}`} ratio={i % 2 === 0 ? 3 / 4 : 4 / 3} sizes="(max-width: 768px) 100vw, 50vw" className="w-full h-full object-cover" style={{ filter: IMG_FILTER }} />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* La autoría */}
      {mostrarAutoria && (
        <section className="py-16 md:py-28 px-6 md:px-12" style={{ background: C.bg2 }} data-testid="casa-authorship">
          <div className={`max-w-[1400px] mx-auto grid gap-12 md:gap-20 items-center ${house.authorship_image ? 'md:grid-cols-2' : ''}`}>
            {house.authorship_image && <div className="relative overflow-hidden order-last md:order-first" style={{ aspectRatio: '4/5' }}><Imagen src={house.authorship_image} alt={house.authorship_studio} ratio={4 / 5} sizes="(max-width: 768px) 100vw, 50vw" className="w-full h-full object-cover" style={{ filter: IMG_FILTER }} /></div>}
            <div>
              <p style={{ color: C.gold, letterSpacing: TRACK.eyebrow }} className="text-[13px] uppercase">{t.authorship}</p>
              {!sinDefinir(house.authorship_studio) && <h3 className="mt-5 text-4xl md:text-5xl" style={{ fontFamily: SERIF, fontWeight: 300 }}>{house.authorship_studio}</h3>}
              {!sinDefinir(house.authorship_studio) && <p className="mt-3 text-[13px] uppercase" style={{ color: C.ink40, letterSpacing: TRACK.caption }}>{t.studioLabel}</p>}
              {!sinDefinir(house.authorship_text) && <p className="mt-6 text-lg leading-relaxed max-w-md" style={{ color: C.ink60 }}>{house.authorship_text}</p>}
            </div>
          </div>
        </section>
      )}

      {/* Datos esenciales */}
      <section className="py-16 md:py-24 px-6 md:px-12" data-testid="casa-essentials">
        <div className="max-w-[1400px] mx-auto">
          <p style={{ color: C.gold, letterSpacing: TRACK.eyebrow }} className="text-[13px] uppercase mb-10">{t.essentials}</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px" style={{ background: C.line }}>
            {[
              { label: t.sqm, value: specs.sqm },
              { label: t.bedrooms, value: specs.bedrooms },
              { label: t.weeks, value: specs.weeks },
              { label: t.shares, value: specs.shares },
            ].map((d, i) => (
              <div key={d.label} className="p-6 md:p-8" style={{ background: C.bg }}>
                <p className="text-xl md:text-2xl" style={{ fontFamily: MONO, fontWeight: 400 }}>{d.value || '—'}</p>
                <p className="mt-3 text-[13px] uppercase" style={{ color: C.ink40, letterSpacing: TRACK.caption }}>{d.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mapa discreto */}
      {house.map_query && (
        <section className="pb-16 md:pb-24 px-6 md:px-12" data-testid="casa-map">
          <div className="max-w-[1400px] mx-auto">
            <p style={{ color: C.gold, letterSpacing: TRACK.eyebrow }} className="text-[13px] uppercase mb-6">{t.map}</p>
            <div className="relative overflow-hidden" style={{ border: `1px solid ${C.line}` }}>
              <iframe
                title={`Mapa ${house.name}`}
                src={`https://www.google.com/maps?q=${encodeURIComponent(house.map_query)}&z=13&output=embed`}
                className="w-full"
                style={{ height: 360, border: 0, filter: 'grayscale(1) contrast(0.9) opacity(0.85)' }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </section>
      )}

      {/* CTA dossier */}
      <section id="dossier" className="py-16 md:py-28 px-6 md:px-12" style={{ background: C.accent, color: C.linen, scrollMarginTop: 96 }} data-testid="casa-dossier">
        <div className="max-w-[1100px] mx-auto grid md:grid-cols-2 gap-14 lg:gap-24 items-start">
          <div>
            <h2 className="text-3xl md:text-5xl leading-[1.15]" style={{ fontFamily: SERIF, fontWeight: 300 }}>{t.dossierCta}</h2>
            <p className="mt-6 text-base leading-relaxed" style={{ color: C.linen82, maxWidth: MEDIDA }}>{t.dossierIntro}</p>
            <p className="mt-8 text-[13px] uppercase" style={{ color: C.linen45, letterSpacing: TRACK.caption }}>SOHA Reserve &middot; {house.name} &middot; {house.location}</p>
          </div>
          <FormularioHubSpot propiedad={PROPIEDAD_HUBSPOT[slug]} lang={lang} />
        </div>
      </section>

      {/* Footer minimal */}
      <footer className="py-12 px-6 md:px-12 text-center" style={{ background: C.bg2 }} data-testid="casa-footer">
        <Link to="/"><img src="/brand/soha-principal-verde.png" alt="SOHA Reserve" width="800" height="318" className="h-16 w-auto object-contain mx-auto mb-4" style={{ filter: 'none' }} /></Link>
        <button onClick={() => navigate('/')} className="text-xs tracking-[0.14em] uppercase hover:opacity-60 transition-opacity" style={{ color: C.ink60 }}>{t.back}</button>
      </footer>

      <style>{`
        @media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation: none !important; transition: none !important; } }
        .casa-field:focus { border-color: var(--soha-marfil) !important; }
        .casa-field:focus-visible { outline: 2px solid var(--soha-dorado); outline-offset: 3px; }
      `}</style>
    </div>
  );
}
